import { DatePickerTR } from "@/components/DatePickerTR";
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { fmtTL } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { History, Search, TrendingUp, ShoppingCart, ChevronLeft, ChevronRight, Loader2, ArrowRight } from "lucide-react";

const CH_LABELS = { market_retail: "Market", market_web: "E-Ticaret", marketplace: "Pazaryeri", wholesale_pickup: "Depo/Toptan", wholesale_delivered: "Rota" };

export default function PriceHistory() {
  const [sp] = useSearchParams();
  const productId = sp.get("product") || "";
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 25 };
      if (productId) params.product_id = productId;
      if (q) params.q = q;
      if (kind) params.kind = kind;
      if (start) params.start = start;
      if (end) params.end = end;
      const { data } = await api.get("/price-history", { params });
      setData(data);
    } catch (e) { setData({ items: [], total: 0, pages: 1, page: 1 }); }
    finally { setLoading(false); }
  }, [page, productId, q, kind, start, end]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, kind, start, end]);

  const items = data?.items || [];
  return (
    <div className="space-y-5 fade-in" data-testid="price-history-page">
      <div>
        <p className="overline">Groot Market · Fiyat Geçmişi</p>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-black tracking-tight text-slate-900">
          <History className="h-6 w-6 text-brand-green" /> Fiyat Geçmişi
        </h1>
        <p className="mt-1 text-sm text-slate-500">Her ürünün tüm alış ve satış fiyat değişimleri — kalıcı, filtrelenebilir tam kayıt (append-only).{productId ? " · Tek ürün filtresi aktif." : ""}</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="min-w-[220px] flex-1">
          <label className="text-xs font-semibold text-slate-500">Ara (ürün / SKU / tedarikçi)</label>
          <div className="relative mt-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input data-testid="ph-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün adı, SKU veya tedarikçi" className="pl-8" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Tür</label>
          <div className="mt-1 flex gap-1">
            {[["", "Tümü"], ["purchase", "Alış"], ["sale", "Satış"]].map(([v, l]) => (
              <button key={v} data-testid={`ph-kind-${v || "all"}`} onClick={() => setKind(v)}
                className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors ${kind === v ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Başlangıç</label>
          <DatePickerTR testid="ph-start" value={start} onChange={(v) => setStart(v)} />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Bitiş</label>
          <DatePickerTR testid="ph-end" value={end} onChange={(v) => setEnd(v)} />
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="ph-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Tarih</th>
              <th className="px-4 py-2.5">Ürün</th>
              <th className="px-4 py-2.5">Tür</th>
              <th className="px-4 py-2.5">Kanal / Tedarikçi</th>
              <th className="px-4 py-2.5 text-right">Değişim</th>
              <th className="px-4 py-2.5">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Kayıt bulunamadı. Fiyat değiştikçe (mal kabul / satış fiyatı güncelleme) buraya işlenir.</td></tr>}
            {!loading && items.map((e) => (
              <tr key={e.id} data-testid={`ph-row-${e.id}`} className="hover:bg-slate-50">
                <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">{e.created_at ? new Date(e.created_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                <td className="px-4 py-2.5"><span className="font-medium text-slate-800">{e.name}</span>{e.sku ? <span className="ml-1 text-xs text-slate-400">{e.sku}</span> : null}</td>
                <td className="px-4 py-2.5">
                  {e.kind === "purchase"
                    ? <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700"><ShoppingCart className="h-3 w-3" /> Alış</span>
                    : <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700"><TrendingUp className="h-3 w-3" /> Satış</span>}
                </td>
                <td className="px-4 py-2.5 text-slate-600">{e.kind === "purchase" ? (e.supplier || "—") : (CH_LABELS[e.channel] || e.channel || "—")}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right">
                  {e.kind === "purchase"
                    ? <span className="tabular font-bold text-slate-800">{fmtTL(e.unit_cost)}{e.quantity ? <span className="ml-1 text-xs font-normal text-slate-400">× {e.quantity}</span> : null}</span>
                    : <span className="tabular inline-flex items-center gap-1.5">
                        {e.old_price != null && <span className="text-slate-400 line-through">{fmtTL(e.old_price)}</span>}
                        <ArrowRight className="h-3 w-3 text-slate-300" />
                        <span className={`font-bold ${e.old_price != null && e.new_price > e.old_price ? "text-red-600" : "text-emerald-700"}`}>{fmtTL(e.new_price)}</span>
                      </span>}
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-400">{e.actor || e.source || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">Toplam {data.total} kayıt · Sayfa {data.page}/{data.pages}</p>
          <div className="flex gap-2">
            <Button data-testid="ph-prev" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}><ChevronLeft className="h-4 w-4" /></Button>
            <Button data-testid="ph-next" variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
